/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/non-nullable-type-assertion-style */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useState } from 'react';
import { type Customer } from '../store/customerTypes';
import { Avatar, Table, Modal } from 'antd';
import dayjs from 'dayjs';
import fmt from 'indian-number-format';

interface Sale {
  _id: string;
  saleNumber: string;
  customer: any;
  principal: number;
  onlinePrincipal?: number;
  offlinePrincipal?: number;
  documentCharge: number;
  status: string;
  photo: string;
  startDate: string;
  branch: any;
  metal: string;
  jewels: any[];
  remarks: string;
  createdDate: string;
  remainingPrincipal: number;
  transactions: any[];
  [key: string]: any;
}

interface Props {
  saleDetail?: Sale;
  customer?: Customer;
  transactions?: any[];
  openPrintModal: (transaction: any) => void;
}

export default function SaleModal({
  saleDetail,
  customer,
  transactions = [],
  openPrintModal,
}: Props): JSX.Element {
  const pledgeNumbers = new Set(
    saleDetail?.jewels
      .filter((jewel: any) => jewel.isPledged)
      .map((jewel: any) => jewel.pledgeNumber)
  );

  const totals = transactions.reduce(
    (acc: any, txn: any) => {
      acc.principal += txn.principal || 0;
      return acc;
    },
    { principal: 0 }
  );

  const jewelColumns = [
    {
      title: 'S.No',
      dataIndex: '_id',
      key: 'index',
      render: (_: any, record: any, index: number) => <p>{index + 1}</p>,
    },
    {
      title: 'Item Type',
      dataIndex: 'itemType',
      key: 'itemType',
      render: (_: any, record: any) => <p>{record.itemType}</p>,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (_: any, record: any, index: number) => <p>{record.quantity}</p>,
    },
    {
      title: 'Gross Weight',
      dataIndex: 'grossWeight',
      key: 'grossWeight',
      render: (_: any, record: any, index: number) => (
        <p>{parseFloat(Number(record.grossWeight).toFixed(2))}</p>
      ),
    },
    {
      title: 'Net Weight',
      dataIndex: 'netWeight',
      key: 'netWeight',
      render: (_: any, record: any, index: number) => (
        <p>{parseFloat(Number(record.netWeight).toFixed(2))}</p>
      ),
    },
    {
      title: 'Purity',
      dataIndex: 'purity',
      key: 'purity',
      render: (_: any, record: any, index: number) => <p>{record.purity}</p>,
    },

    {
      title: 'Sale Price',
      dataIndex: 'salePrice',
      key: 'salePrice',
      render: (_: any, record: any, index: number) => (
        <p>{record.salePrice ? `₹${fmt.format(record.salePrice)}` : '-'}</p>
      ),
    },
  ];

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'transactionDate',
      key: 'index',
      render: (_: any, record: any, index: number) => <p>{index + 1}</p>,
    },
    {
      title: 'Till Date',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      render: (_: any, record: any) =>
        dayjs(String(record.transactionDate)).format('DD/MM/YYYY'),
    },
    {
      title: 'Transaction amount',
      dataIndex: 'principalPaid',
      key: 'principalPaid',
      render: (_: any, record: any, index: number) => (
        <p>{fmt.format(record.principal)}</p>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (_: any, record: any) =>
        dayjs(String(record.createdAt)).format('DD/MM/YYYY'),
    },
    {
      title: 'Payment Type',
      dataIndex: 'paymentType',
      key: 'paymentType',
      render: (_: any, record: any, index: number) => (
        <p>{record.paymentType?.toUpperCase()}</p>
      ),
    },
    {
      title: 'Payment status',
      dataIndex: 'status',
      key: 'status',
      render: (_: any, record: any, index: number) => (
        <p>
          {record.status
            ? record.status.toUpperCase().replace('_', ' ')
            : 'Cash Received'}
        </p>
      ),
    },
  ];

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string | undefined>('');

  const handleImageClick = (src: string | undefined) => {
    setImageSrc(src);
    setIsModalVisible(true);
  };

  const handleClose = () => {
    setIsModalVisible(false);
    setImageSrc('');
  };

  return (
    <div className="loan__modal">
      <div className="divider" />
      <div className="profile__section">
        <Avatar
          onClick={() => {
            handleImageClick(saleDetail?.photo);
          }}
          size={64}
          icon={<img src={saleDetail?.photo} />}
        />
        <Modal
          visible={isModalVisible}
          footer={null}
          onCancel={handleClose}
          centered
        >
          <img
            src={imageSrc}
            alt="Full Size"
            style={{ width: '100%', height: '100%' }}
          />
        </Modal>
        <div className="profile__section__details">
          <p className="customer__name">{customer?.name}</p>
          <p className="customer__id">{customer?.customerId}</p>
        </div>
      </div>
      <div className="profile__details">
        <div className="detail">
          <p className="detail__title">Sale No.</p>
          <p className="data">{saleDetail?.saleNumber}</p>
        </div>
        <div className="profile__divider" />
        <div className="detail">
          <p className="detail__title">Pledge No.</p>
          <p style={{ display: 'flex', flexDirection: 'column' }}>
            {Array.from(pledgeNumbers).map((x) => (
              <p className="data" style={{ margin: 0 }} key={x}>
                {x}
              </p>
            ))}
          </p>
        </div>
        <div className="profile__divider" />
        <div className="detail">
          <p className="detail__title">Bank Branch</p>
          <p className="data">{saleDetail?.branch?.code}</p>
        </div>
        <div className="profile__divider" />
        <div className="detail">
          <p className="detail__title">Start Date</p>
          <p className="data">
            {dayjs(saleDetail?.startDate as string).format('DD MMM YYYY')}
          </p>
        </div>
        <div className="profile__divider" />
        <div className="detail">
          <p className="detail__title">Principal</p>
          <p className="data">{fmt.format(saleDetail?.principal)}</p>
        </div>
        <div className="profile__divider" />
        <div className="detail">
          <p className="detail__title">Type</p>
          <p className="data">{saleDetail?.metal}</p>
        </div>
      </div>
      <div>
        <h2>Jewels</h2>
        <Table
          dataSource={saleDetail?.jewels}
          columns={jewelColumns}
          pagination={false}
        ></Table>
      </div>
      <div className="divider" />
      {Boolean(transactions.length) && (
        <div>
          <h2>Transactions </h2>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 32 }}>
            <p>Transaction amount: {fmt.format(totals.principal)}</p>
          </div>
          <Table
            dataSource={transactions}
            columns={columns}
            pagination={false}
          ></Table>
        </div>
      )}
    </div>
  );
}
